import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { NotificationsService } from 'angular2-notifications';
import { AssignService } from 'src/app/core/services/ticket/assign.service';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { RightPanelService } from '../../core/services/right-panel.service';
import { environment } from '@environment';
import { TicketRightPanelService } from '../../core/services/ticket-right-panel.service';
import { TicketTabType } from '../../core/enums/ticket-tab-type.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { getSourceNameUtil } from '../../core/utils/get-source-name.util';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { DatePipe } from '@angular/common';
import { TicketSourcePipe } from '../../../sb-tools/typescript/pipes/ticket-source.pipe';

@Component({
    selector: 'app-ticket-alerts',
    templateUrl: './ticket-alerts.component.html',
    styleUrls: ['./ticket-alerts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [InlineSVGComponent, TranslateModule, DatePipe, TicketSourcePipe],
})
export class TicketAlertsComponent implements OnChanges, OnDestroy {
    @Input() assigneeId?: string;
    @Input() isCustomerOnline: boolean;
    @Input() isSocialMediaTicket: boolean;
    @Input() ticket: DetailedTicketDTO;
    @Input() ticketStatus: TicketStatus;
    @Input() ticketUpdateStamp: number;

    @Output() assignTicketFromAlert = new EventEmitter();
    @Output() restoreTicketFromAlert = new EventEmitter<void>();

    isAssignedToAnotherUserAlert: boolean;
    isCustomerLeftChatAlert: boolean;
    isNeedToAnswerByPermalink: boolean;
    isNeedToAddUserInfo: boolean;
    isNoSocialMediaSourceInstalledAlert: boolean;
    isPostHiddenAlert: boolean;
    isReplyAddingBlockedWhenChatIsEndedAlert: boolean;
    isTakeOverAlert: boolean;
    isTicketAssigningObligatoryAlert: boolean;
    isTicketClosedOrMovedToSpamAlert: boolean;
    rightPanelTypeEnum = RightPanelType;
    ticketStatusEnum = TicketStatus;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public assignService: AssignService,
        public rightPanelService: RightPanelService,
        private ticketService: TicketService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private ticketRightPanelService: TicketRightPanelService,
    ) {}

    ngOnChanges() {
        if (!this.ticket.isAssignedToBot) {
            this.isAssignedToAnotherUserAlert = this.ticket.isAssigned && !this.assignService.isAssignedToCurrentUser;

            this.isNoSocialMediaSourceInstalledAlert =
                !this.ticket.defaultSocialMediaSourceId && this.isSocialMediaTicket && !this.ticket.isEditable;

            this.isNeedToAnswerByPermalink =
                this.ticket.defaultSocialMediaSourceId && this.ticket.permalink && !this.ticket.isEditable;
        }

        this.isPostHiddenAlert = this.ticket.isHidden && !this.ticket.isReviewNeeded;

        this.isTakeOverAlert = this.ticket.isAssignedToBot && this.ticket.constraints.isTakeOverAllowed;

        this.isTicketAssigningObligatoryAlert =
            this.commonDataService.customerSettings.isTicketAssigningObligatory && !this.ticket.isAssigned;

        this.isTicketClosedOrMovedToSpamAlert =
            this.ticket.status === this.ticketStatusEnum.completed || this.ticket.isSpam;

        this.isCustomerLeftChatAlert =
            this.ticket.detailedSource === DetailedTicketSource.chat &&
            !this.assignService.isNotAssignedToCurrentUser &&
            !this.ticket.constraints.isTicketStatusImmutableAndConnectedToChatEnd &&
            !this.isCustomerOnline;

        this.isReplyAddingBlockedWhenChatIsEndedAlert =
            this.isCustomerLeftChatAlert && this.ticket.constraints.isReplyAddingBlockedWhenChatIsEnded;

        this.isNeedToAddUserInfo =
            this.ticket.constraints.isFirstMessageCreatorEditable && !this.ticket.firstMessageCreator?.email;
    }

    assignTicket(event) {
        this.assignTicketFromAlert.next(event);
    }

    deleteReminder(): void {
        this.ticketService
            .deleteReminder(this.ticket.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.ticket.reminder = null;
                this.ticketUpdateStamp = new Date().getTime();
            });
    }

    openEditUserForm(): void {
        if (this.rightPanelService.isRightPanelOpened) {
            this.rightPanelService.close();
        }

        this.ticketRightPanelService.open({ tabType: TicketTabType.ticketAuthor, isShowEditUserForm: true });
        this.ticketRightPanelService.openUserEditFormSubject.next();
    }

    openInstallationPage(source: number): void {
        window.open(
            environment.settingsUrl +
                this.commonDataService.communityName +
                '/community/installation/' +
                getSourceNameUtil(source),
            '_blank',
        );
    }

    restore() {
        this.restoreTicketFromAlert.next();
    }

    takeOverTicket() {
        this.ticketService
            .takeOver(this.ticket.id, false)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                // todo: check this status 0
                if (data.status === 0) {
                    this.ticket.isAssignedToBot = false;
                    this.ticket.isAssigned = true;
                    this.ticket.assignee = this.commonDataService.user;
                    this.commonDataService.isTicketTakenOverFromBot = true;
                    this.ticket.isEditable = true;

                    this.router
                        .navigate([], {
                            relativeTo: this.activatedRoute,
                            queryParams: { status: TicketStatus.open },
                        })
                        .then(() => {
                            setTimeout(() => {
                                this.router.navigate([], {
                                    relativeTo: this.activatedRoute,
                                    queryParams: { ticketId: this.ticket.id },
                                    queryParamsHandling: 'merge',
                                });
                            }, 100);
                        });
                } else {
                    this.notificationsService.warn(
                        this.translateService.instant('Warning!'),
                        this.translateService.instant(
                            "Cannot take over ticket. Bot doesn't have enough info about the user.",
                        ),
                        {
                            timeOut: 10000,
                        },
                    );
                }

                return false;
            });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
